import { useRef, useEffect, useMemo } from 'react';
import { Chart, registerables, ChartData, ChartOptions, Point, BubbleDataPoint } from 'chart.js';
import ChartLegend from '../chartLegend/ChartLegend';
import { valueLabelsColumn } from './chartPlugins';
import React from 'react';

Chart.register(...registerables);
Chart.defaults.font.family = 'Arial';
Chart.defaults.font.size = 14;
Chart.defaults.color = '#111928';

type DataValue = {
  value: number;
  label: string;
};

type ChartActionsProps = {
  data: DataValue[];
  openActions: DataValue[];
  closeActions: DataValue[];
};

function ChartActions({ data, openActions, closeActions }: ChartActionsProps) {
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  //! Опр текущий квартал
  const currentQuarter = Math.ceil((new Date().getMonth() + 1) / 3);

  const maxYAverageValue = Math.ceil(Math.max(...data.map((item) => item.value)) / 20) * 20;
  const maxValue = Math.max(...openActions.map((item) => item.value), ...closeActions.map((item) => item.value));

  const maxYActions = (() => {
    if (maxValue <= 50) {
      return Math.ceil(maxValue / 10) * 10; 
    } else {
      return Math.ceil(maxValue / 20) * 20; 
    }
  })();

  const chartData: ChartData = useMemo(
    () => ({
      labels: ['I кв.', 'II кв.', 'III кв.', 'IV кв.'],
      datasets: [
       
        {
          label: 'Открытые действия в шт',
          type: 'line',
          data: openActions.map((item) => item.value),
          backgroundColor: '#06B6D4',
          borderColor: '#06B6D4',
          borderWidth: 3,
          yAxisID: 'y1',
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: '#06B6D4',
          pointBorderColor: '#06B6D4',
          tension: 0,
        },
        {
          label: 'Закрытые действия в шт',
          type: 'line',
          data: closeActions.map((item) => item.value),
          backgroundColor: '#F59E0B',
          borderColor: '#F59E0B',
          borderWidth: 3,
          yAxisID: 'y1',
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: '#F59E0B',
          pointBorderColor: '#F59E0B',
          tension: 0,
        },
         {
          label: 'Направление в %',
          data: data.map((item) => item.value),
          backgroundColor: '#8B5CF6',
          borderColor: '#8B5CF6',
          borderSkipped: false,
          borderRadius: 4,
          categoryPercentage: 0.7,
          barPercentage: 0.8,
          yAxisID: 'y2',
        },
      ],
    }),
    [data, openActions, closeActions]
  );

  const chartOptions: ChartOptions = useMemo(
  () => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        mode: 'nearest', 
        intersect: true,  
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 6,
        displayColors: true,
        filter: function(tooltipItem: any) {
          return true;
        },
        callbacks: {
          title: function(context: any) {
            if (context.length > 0) {
              return context[0].label; 
            }
            return '';
          },
          label: function (context: any) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            
            if (label.includes('%')) {
              return `${label}: ${value}%`;
            }
            return `${label}: ${value} шт`;
          },
          afterLabel: function() {
            return '';
          }
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: function (context: any) {
            const index = context.index;
            return index < currentQuarter ? '#374151' : 'rgba(55, 65, 81, 0.4)';
          },
          font: {
            size: 12,
            weight: 'normal',
          },
        },
        border: {
          display: false,
        },
      },
      y1: {
        beginAtZero: true,
        position: 'left',
        ticks: {
          callback: (value: any) => `${value} шт`,
          stepSize: 10,
          color: '#9CA3AF',
          font: {
            size: 11,
          },
        },
        max: maxYActions,
        grid: {
          color: 'rgba(156, 163, 175, 0.3)',
          lineWidth: 1,
        },
        border: {
          display: false,
        },
      },
      y2: {
        beginAtZero: true,
        position: 'right',
        ticks: {
          callback: (value: any) => `${value}%`,
          stepSize: 20,
          color: '#9CA3AF',
          font: {
            size: 11,
          },
        },
        max: maxYAverageValue,
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
      },
    },
    interaction: {
      mode: 'nearest',
      intersect: true,
    },
    elements: {
      point: {
        borderWidth: 0,
        hoverRadius: 8,
      },
      line: {
        borderJoinStyle: 'round',
      },
      bar: {
        hoverBackgroundColor: '#7C3AED', 
        hoverBorderColor: '#7C3AED',
      },
    },
  }),
  [currentQuarter, maxYActions, maxYAverageValue]
);

  const legendLabels = chartData.datasets.map((dataset) => ({
    text: dataset.label as string,
    color: dataset.backgroundColor as string,
  }));

  useEffect(() => {
    if (chartRef.current) {
      const ctx = chartRef.current.getContext('2d');

      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }

      chartInstanceRef.current = new Chart(ctx as CanvasRenderingContext2D, {
        type: 'bar',
        data: chartData,
        options: chartOptions,
        plugins: [valueLabelsColumn],
      });
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [chartData, chartOptions]);

  return (
    <div className='w-full'>
      <div className='h-96 mb-4'>
        <canvas ref={chartRef} />
      </div>
      <div className='flex justify-center'>
        <ChartLegend labels={legendLabels} />
      </div>
    </div>
  );
}

export default React.memo(ChartActions);
